import {
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObject,
  ProcedureValidationObjectType,
  VALIDATION_ERROR_WITH_NO_DISPLAYED_OBJECT,
} from 'procedures/design/validation/ProcedureValidationTypes';
import {isProcedureValidationContext} from 'procedures/design/validation/procedureValidationUtils';
import {validateProcessFlowchartForProcedure} from 'procedures/design/validation/validateProcedure';
import {MethodNode} from 'procedures/ProcedureDomainTypes';
import {isMethodNode} from 'procedures/processes/MethodNodeUtils';
import {healDataFlowEdges} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/healFlowchartUtils';
import {FlowchartValidator} from 'processes/graph-builder-processes/platform/FlowchartValidator';
import {
  FlowchartValidationObjectType,
  ProcessFlowchartErrorType,
  ProcessFlowchartForValidation,
  ProcessFlowchartObjectType,
  ProcessFlowchartValidationError,
  ValidationContext,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {TaskDefinitionVersionSpecWithTaskSchemaFlowchart} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {getApplicationDataSafely} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {
  FlowchartChangeGenerator,
  FlowchartChanges,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useFlowchartState/FlowchartStateTypes';

/**
 * Maps procedure validation error types to whether they block persistence. These are only applicable to
 * procedure application-level errors.
 *
 * Set any error type to TRUE if the workflows platform would be unable to save the procedure. Typically,
 * the only error types that block persistence are those which represent a structurally invalid flowchart.
 *
 * Most procedure-level errors are execution-blocking, rather than persistence-blocking.
 */
const PROCEDURE_VALIDATION_ERROR_TYPE_TO_BLOCK_PERSISTENCE: {
  [key in ProcedureValidationErrorType]: boolean;
} = {
  [ProcedureValidationErrorType.NON_UNIQUE_NAME]: false,
  [ProcedureValidationErrorType.INVALID_NAME]: false,
  [ProcedureValidationErrorType.DELETED_PARAMETER]: false,
  [ProcedureValidationErrorType.PARAMETER_LIBRARY_LINK_REQUIRED]: false,
  [ProcedureValidationErrorType.PARAMETER_LIBRARY_LINK_DISALLOWED]: false,
  [ProcedureValidationErrorType.ARCHIVED_LIBRARY_PARAMETER_DEFINITION]: false,
  [ProcedureValidationErrorType.INVALID_PARAMETER_RANGE]: false,
  [ProcedureValidationErrorType.INVALID_SCHEMA_FOR_PARAMETER_TYPE]: false,
  [ProcedureValidationErrorType.SETPOINT_NOT_IN_RANGE]: false,
  [ProcedureValidationErrorType.UPSTREAM_MATERIAL_EDGE]: true,
  [ProcedureValidationErrorType.INDIRECT_MATERIAL_EDGE]: true,
  [ProcedureValidationErrorType.ARCHIVED_MODEL_ENTITY_SCHEMA]: false,
  [ProcedureValidationErrorType.MISSING_MATERIAL_OUTPUT_TYPE]: false,
  [ProcedureValidationErrorType.ARCHIVED_SCHEMA]: false,
  [ProcedureValidationErrorType.MISMATCHED_VALUE_TYPE]: false,
  [ProcedureValidationErrorType.MISMATCH_MODELS_TYPE]: false,
  [ProcedureValidationErrorType.NEGATIVE_AMOUNT]: false,
  [ProcedureValidationErrorType.PROCEDURE_PRODUCT_LIMITS]: false,
  [ProcedureValidationErrorType.NON_ENTITY_MATERIAL_INPUT_CONFIRMED_VIA_PLATE_MAP]: false,
  [ProcedureValidationErrorType.UNCONTAINABLE_SCHEMA_ON_MATERIAL_INPUT_CONFIRMED_VIA_PLATE_MAP]: false,
  [ProcedureValidationErrorType.EQUIPMENT_FIELDSET_AS_MATERIAL_TYPE]: false,
  [ProcedureValidationErrorType.DUPLICATE_TEST_DEFINITION]: false,
  [ProcedureValidationErrorType.DUPLICATE_DILUTION_FACTOR]: false,
  [ProcedureValidationErrorType.INVALID_DILUTION_FACTOR]: false,
  [ProcedureValidationErrorType.DILUTION_PLAN_WITHOUT_TEST_DEFINITIONS]: false,
  [ProcedureValidationErrorType.INVALID_DILUTION_ASSIGNMENT]: false,
  [ProcedureValidationErrorType.NO_UNIT_OPERATIONS]: true,
};

/**
 * Maps procedure validation object types to a possibly overridden ProcessFlowchartObjectType.
 *
 * Some procedure object types (e.g., PROCEDURE, METHOD, MATERIAL_EDGE) map to process flowchart object types
 * (FLOWCHART_CONFIG, NODE, EDGE) because they represent flowchart-level concepts.
 *
 * Other procedure object types (e.g. DATA_PARAMETER, STEP, TEST_DEFINITION, DILUTION_PLAN, etc.) will be
 * represented with their procedure object type. This is fine because these errors will not be surfaced at
 * the flowchart editor level and handled separately within the sidepanel (method editor) for Procedures.
 */
const PROCEDURE_OBJECT_TYPE_TO_MAYBE_OVERRIDDEN_OBJECT_TYPE: {
  [key in ProcedureValidationObjectType]: ProcessFlowchartObjectType | ProcedureValidationObjectType;
} = {
  [ProcedureValidationObjectType.PROCEDURE]: ProcessFlowchartObjectType.FLOWCHART_CONFIG,
  [ProcedureValidationObjectType.METHOD]: ProcessFlowchartObjectType.NODE,
  [ProcedureValidationObjectType.MATERIAL_INPUT]: ProcedureValidationObjectType.MATERIAL_INPUT,
  [ProcedureValidationObjectType.MATERIAL_OUTPUT]: ProcedureValidationObjectType.MATERIAL_OUTPUT,
  [ProcedureValidationObjectType.DATA_PARAMETER]: ProcedureValidationObjectType.DATA_PARAMETER,
  [ProcedureValidationObjectType.EQUIPMENT]: ProcedureValidationObjectType.EQUIPMENT,
  [ProcedureValidationObjectType.MATERIAL_EDGE]: ProcessFlowchartObjectType.EDGE,
  [ProcedureValidationObjectType.STEP]: ProcedureValidationObjectType.STEP,
  [ProcedureValidationObjectType.TEST_DEFINITION]: ProcedureValidationObjectType.TEST_DEFINITION,
  [ProcedureValidationObjectType.DILUTION_PLAN]: ProcedureValidationObjectType.DILUTION_PLAN,
  [ProcedureValidationObjectType.UNKNOWN]: ProcessFlowchartObjectType.UNKNOWN,
};

/**
 * Maps ProcessFlowchartObjectType to ProcedureValidationObjectType.
 *
 * This reverse mapping is needed temporarily to convert processes platform validation errors
 * back to procedure validation errors for MethodEditor, which currently only understands
 * procedure validation errors.
 *
 * TODO(BNCH-141566): This mapping can be removed once MethodEditor is migrated to handle
 * ProcessFlowchartValidationErrors directly.
 */
const PROCESS_OBJECT_TYPE_TO_PROCEDURE_OBJECT_TYPE: Partial<
  Record<ProcessFlowchartObjectType, ProcedureValidationObjectType>
> = {
  [ProcessFlowchartObjectType.FLOWCHART_CONFIG]: ProcedureValidationObjectType.PROCEDURE,
  [ProcessFlowchartObjectType.NODE]: ProcedureValidationObjectType.METHOD,
  [ProcessFlowchartObjectType.EDGE]: ProcedureValidationObjectType.MATERIAL_EDGE,
  [ProcessFlowchartObjectType.UNKNOWN]: ProcedureValidationObjectType.UNKNOWN,
};

export default class ProcedureFlowchartValidator extends FlowchartValidator<
  ProcedureValidationError,
  ProcedureValidationErrorType,
  ProcedureValidationObjectType
> {
  override validateFlowchartForType(
    flowchart: ProcessFlowchartForValidation,
    validationContext: ValidationContext
  ): Array<ProcessFlowchartValidationError> {
    const {applicationValidationContext} = validationContext;
    if (applicationValidationContext == null || !isProcedureValidationContext(applicationValidationContext)) {
      return [];
    }
    const {procedureValidationErrors, methodNodeCidToTaskNodeId} = validateProcessFlowchartForProcedure(
      flowchart,
      applicationValidationContext
    );
    return this.convertApplicationValidationErrorsIntoProcessValidationErrors(
      procedureValidationErrors,
      methodNodeCidToTaskNodeId
    );
  }

  override readonly errorTypeToMessageOverride: Partial<Record<ProcessFlowchartErrorType, string>> = {
    [ProcessFlowchartErrorType.DATA_FLOW_EDGE_WITH_NO_CONTROL_FLOW_EDGE]:
      'Materials can only flow between connected unit operations.',
    [ProcessFlowchartErrorType.DATA_FLOW_EDGE_MAPPING_FIELD_MISMATCH]: 'Materials have mismatched types.',
  };

  override healFlowchart(changes: FlowchartChanges): FlowchartChangeGenerator {
    return healDataFlowEdges(changes);
  }

  protected override getBlocksPersistenceForApplicationErrorType(
    errorType: ProcedureValidationErrorType
  ): boolean {
    return PROCEDURE_VALIDATION_ERROR_TYPE_TO_BLOCK_PERSISTENCE[errorType];
  }

  protected override applicationObjectTypeToMaybeOverriddenProcessObjectType(
    applicationObjectType: ProcedureValidationObjectType
  ): FlowchartValidationObjectType {
    return PROCEDURE_OBJECT_TYPE_TO_MAYBE_OVERRIDDEN_OBJECT_TYPE[applicationObjectType];
  }

  override convertApplicationValidationErrorIntoProcessValidationError(
    procedureValidationError: ProcedureValidationError,
    objectIdOverrides: Record<string, string>
  ): ProcessFlowchartValidationError {
    const objectId =
      objectIdOverrides[procedureValidationError.primaryObject.id] ??
      procedureValidationError.primaryObject.id;

    return {
      object:
        // When the placeholder objectId is used, we want the object to be null. This is the way the processes
        // platform validation knows this error should be displayed at the top level and not associated with a specific object.
        objectId !== VALIDATION_ERROR_WITH_NO_DISPLAYED_OBJECT
          ? {
              clientId: objectId,
              type: this.applicationObjectTypeToMaybeOverriddenProcessObjectType(
                procedureValidationError.primaryObject.objectType
              ),
            }
          : null,
      // Map all parent objects from procedure validation errors to process validation errors
      parentObjects:
        procedureValidationError.parentObjects.length > 0
          ? procedureValidationError.parentObjects.map((parentObject) => ({
              clientId: objectIdOverrides[parentObject.id] ?? parentObject.id,
              type: this.applicationObjectTypeToMaybeOverriddenProcessObjectType(parentObject.objectType),
            }))
          : undefined,
      // Procedures does not have a title for validation errors
      errorTitle: undefined,
      errorMessage: procedureValidationError.errorMessage,
      // Error type can be the same since Processes validation system can support both ProcessFlowchartErrorType and
      // any application-specific error types (e.g., ProcedureValidationErrorType).
      errorType: procedureValidationError.type,
      blocksPersistence: this.getBlocksPersistenceForApplicationErrorType(procedureValidationError.type),
    };
  }

  /**
   * Maps a ProcessFlowchartObjectType or ProcedureValidationObjectType to a ProcedureValidationObjectType.
   *
   * This is the reverse mapping of applicationObjectTypeToMaybeOverriddenProcessObjectType.
   * If the input is already a ProcedureValidationObjectType (e.g., DATA_PARAMETER, STEP),
   * it is returned unchanged. Otherwise, it maps ProcessFlowchartObjectType values using
   * PROCESS_OBJECT_TYPE_TO_PROCEDURE_OBJECT_TYPE, falling back to UNKNOWN for unmapped types.
   *
   * This is needed temporarily to convert processes validation errors back to procedure validation errors
   * for MethodEditor, which currently only understands ProcedureValidationErrors.
   *
   * TODO(BNCH-141566): This mapping can be removed once MethodEditor is migrated to handle
   * ProcessFlowchartValidationErrors directly.
   *
   * @param processObjectType - The object type to map (either ProcessFlowchartObjectType or ProcedureValidationObjectType)
   * @returns The corresponding ProcedureValidationObjectType
   */
  private static mapProcessObjectTypeToProcedureObjectType(
    processObjectType: ProcessFlowchartObjectType | ProcedureValidationObjectType
  ): ProcedureValidationObjectType {
    // If it's already a ProcedureValidationObjectType, return it as-is
    // (e.g., DATA_PARAMETER, STEP, TEST_DEFINITION are not overridden in the forward mapping)
    if (
      Object.values(ProcedureValidationObjectType).includes(
        processObjectType as ProcedureValidationObjectType
      )
    ) {
      return processObjectType as ProcedureValidationObjectType;
    }
    // Map ProcessFlowchartObjectType to ProcedureValidationObjectType, or fallback to UNKNOWN
    return (
      PROCESS_OBJECT_TYPE_TO_PROCEDURE_OBJECT_TYPE[processObjectType as ProcessFlowchartObjectType] ??
      ProcedureValidationObjectType.UNKNOWN
    );
  }

  /**
   * Converts a single ProcessFlowchartValidationError back to a ProcedureValidationError.
   *
   * Filters out errors that are not ProcedureValidationErrorType (e.g., ProcessFlowchartErrorType errors
   * like NODE_NOT_CONNECTED). Converts object IDs from task node IDs to method CIDs where applicable,
   * and maps object types from ProcessFlowchartObjectType to ProcedureValidationObjectType.
   *
   * This is needed temporarily to convert processes validation errors back to procedure validation errors
   * for MethodEditor, which currently only understands ProcedureValidationErrors.
   *
   * TODO(BNCH-141566): This conversion can be removed once MethodEditor is migrated to handle
   * ProcessFlowchartValidationErrors directly.
   *
   * @param error - The ProcessFlowchartValidationError to convert
   * @param taskNodeIdToMethodCidMap - Map from task node IDs to method CIDs for ID conversion
   * @returns The converted ProcedureValidationError, or null if the error type is not a ProcedureValidationErrorType
   */
  private static convertProcessValidationErrorToProcedureError(
    error: ProcessFlowchartValidationError,
    taskNodeIdToMethodCidMap: Record<string, string>
  ): ProcedureValidationError | null {
    // Only process errors where errorType is a ProcedureValidationErrorType
    // Filter out ProcessFlowchartErrorType errors (e.g., NODE_NOT_CONNECTED, NO_OUTPUT_NODE)
    if (
      !Object.values(ProcedureValidationErrorType).includes(error.errorType as ProcedureValidationErrorType)
    ) {
      return null;
    }

    let primaryObject: ProcedureValidationObject;
    if (error.object == null) {
      // Use placeholder for errors with no displayed object
      primaryObject = {
        id: VALIDATION_ERROR_WITH_NO_DISPLAYED_OBJECT,
        objectType: ProcedureValidationObjectType.PROCEDURE,
      };
    } else {
      // Map task node ID to method CID if it's a node
      let objectId = error.object.clientId;
      if (error.object.type === ProcessFlowchartObjectType.NODE) {
        const methodCid = taskNodeIdToMethodCidMap[error.object.clientId];
        objectId = methodCid ?? objectId;
      }

      primaryObject = {
        id: objectId,
        objectType: this.mapProcessObjectTypeToProcedureObjectType(error.object.type),
      };
    }

    const parentObjects: Array<ProcedureValidationObject> = [];
    if (error.parentObjects != null && error.parentObjects.length > 0) {
      for (const parentObject of error.parentObjects) {
        let parentId = parentObject.clientId;
        if (parentObject.type === ProcessFlowchartObjectType.NODE) {
          const methodCid = taskNodeIdToMethodCidMap[parentObject.clientId];
          parentId = methodCid ?? parentId;
        }

        parentObjects.push({
          id: parentId,
          objectType: this.mapProcessObjectTypeToProcedureObjectType(parentObject.type),
        });
      }
    }

    return {
      errorMessage: error.errorMessage,
      type: error.errorType as ProcedureValidationErrorType,
      primaryObject,
      parentObjects,
    };
  }

  /**
   * Converts an array of ProcessFlowchartValidationErrors back to ProcedureValidationErrors.
   *
   * This is the reverse of convertApplicationValidationErrorsIntoProcessValidationErrors.
   * Filters to only include errors where errorType is a ProcedureValidationErrorType,
   * and converts them to the ProcedureValidationError format.
   *
   * Builds a mapping from task node IDs to method CIDs by extracting the CID from each
   * method node's application data. This mapping is used to convert object IDs in validation errors
   * from task node IDs (used by the processes platform) to method CIDs (used by the procedure system).
   *
   * This is needed temporarily to convert processes validation errors back to procedure validation errors
   * for MethodEditor, which currently only understands ProcedureValidationErrors.
   *
   * TODO(BNCH-141566): This conversion can be removed once MethodEditor is migrated to handle
   * ProcessFlowchartValidationErrors directly.
   *
   * @param errors - Array of ProcessFlowchartValidationErrors from the processes platform
   * @param flowchart - The flowchart containing nodes to map task node IDs to method CIDs
   * @returns Array of ProcedureValidationErrors (filtered to only ProcedureValidationErrorType errors)
   */
  static convertProcessValidationErrorsToProcedureErrors(
    errors: Array<ProcessFlowchartValidationError>,
    flowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart<unknown>
  ): Array<ProcedureValidationError> {
    const taskNodeIdToMethodCidMap = Object.entries(flowchart.nodes).reduce<Record<string, string>>(
      (acc, [taskNodeId, taskDefinitionVersionSpec]) => {
        if (isMethodNode(taskDefinitionVersionSpec)) {
          acc[taskNodeId] = getApplicationDataSafely<MethodNode>(taskDefinitionVersionSpec).cid;
        }
        return acc;
      },
      {}
    );

    return errors
      .map((error) => this.convertProcessValidationErrorToProcedureError(error, taskNodeIdToMethodCidMap))
      .filter((error): error is ProcedureValidationError => error != null);
  }
}
